import React from "react";
import { normalizeDate } from "../../../utilities";

/* Shared pieces for the window RFQ detail screens (quoter/admin view and
   sales view) so both sides see the same status language and chat layout. */

// Translucent tints so the badges read on glass as well as solid cards.
export const STATUS_BADGES = {
  Submitted: "bg-blue-500/15 text-blue-700 dark:text-blue-300",
  "Under Review": "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  "Awaiting Sales Approval":
    "bg-indigo-500/15 text-indigo-700 dark:text-indigo-300",
  "Needs Revision": "bg-rose-500/15 text-rose-700 dark:text-rose-300",
  "Reviewing Revisions":
    "bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-300",
  Approved: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  "Converted to Order": "bg-teal-500/15 text-teal-700 dark:text-teal-300",
  Cancelled: "bg-slate-500/15 text-slate-600 dark:text-slate-300",
};

export const StatusBadge = ({ status }) => (
  <span
    className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ${
      STATUS_BADGES[status] ||
      "bg-slate-500/15 text-slate-600 dark:text-slate-300"
    }`}
  >
    {status}
  </span>
);

// submit_user / message.user are objects on newer entries, plain strings on
// older ones.
export const personName = (userish) =>
  userish?.name ||
  userish?.Name ||
  (typeof userish === "string" && userish ? userish : "Unknown");

// Staff names come from the user directory in ALL CAPS.
export const titleCaseName = (name = "") =>
  name.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());

export const parseRevisionMessage = (message) => {
  if (!message?.message) {
    return null;
  }
  try {
    const parsed = JSON.parse(message.message);
    if (parsed?.type === "revision") {
      return parsed;
    }
  } catch (error) {
    return null;
  }
  return null;
};

const WORKFLOW_STEPS = [
  { status: "Submitted", label: "Submitted" },
  { status: "Under Review", label: "In Review" },
  { status: "Awaiting Sales Approval", label: "Quote Sent" },
  { status: "Approved", label: "Approved" },
  { status: "Converted to Order", label: "Ordered" },
];

const STEP_INDEX = {
  Submitted: 0,
  "Under Review": 1,
  "Needs Revision": 1,
  "Reviewing Revisions": 1,
  "Awaiting Sales Approval": 2,
  Approved: 3,
  "Converted to Order": 4,
};

const STEPPER_NOTES = {
  "Needs Revision": {
    tint: "border-amber-500/40 bg-amber-500/10",
    team: "Changes requested — waiting on sales to update this RFQ.",
    sales:
      "The window team requested changes — edit the RFQ below and submit a revision.",
  },
  "Reviewing Revisions": {
    tint: "border-fuchsia-500/40 bg-fuchsia-500/10",
    team: "Sales submitted a revision — review the changes, then send an updated quote.",
    sales: "Your revision was sent to the window team for review.",
  },
  Cancelled: {
    tint: "border-slate-400/40 bg-slate-400/10",
    team: "This RFQ was cancelled.",
    sales: "This RFQ was cancelled.",
  },
};

export const WorkflowStepper = ({ status, audience = "team", detail }) => {
  const currentStatus = status || "Submitted";
  const cancelled = currentStatus === "Cancelled";
  const stepIndex = cancelled ? -1 : STEP_INDEX[currentStatus] ?? 0;
  const note = STEPPER_NOTES[currentStatus];

  return (
    <div>
      <div className="flex">
        {WORKFLOW_STEPS.map((step, index) => {
          const done = index < stepIndex;
          const current = index === stepIndex;
          return (
            <div
              key={step.status}
              className="relative flex flex-1 flex-col items-center"
            >
              {index > 0 && (
                <span
                  aria-hidden="true"
                  className={`absolute right-1/2 top-[13px] h-0.5 w-full rounded-full ${
                    index <= stepIndex ? "bg-sky-500" : "bg-slate-400/25"
                  }`}
                />
              )}
              <span
                className={`z-10 flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold tabular-nums ${
                  done || current
                    ? "bg-sky-600 text-white"
                    : "app-muted bg-slate-400/20"
                } ${current ? "ring-4 ring-sky-500/25" : ""}`}
              >
                {done ? "✓" : index + 1}
              </span>
              <span
                className={`mt-1.5 text-center text-[11px] leading-tight sm:text-xs ${
                  current ? "font-semibold" : "app-muted"
                }`}
              >
                {step.label}
              </span>
            </div>
          );
        })}
      </div>
      {note && (
        <div
          className={`mt-4 rounded-xl border px-4 py-2.5 text-sm ${note.tint}`}
        >
          <p>{note[audience] || note.team}</p>
          {detail && <p className="app-muted mt-1 text-xs">{detail}</p>}
        </div>
      )}
    </div>
  );
};

const RevisionChangesTable = ({ changes }) => (
  <div className="app-surface overflow-hidden rounded-lg border">
    <div className="overflow-x-auto">
      <table className="app-table text-xs">
        <thead>
          <tr>
            <th>Field</th>
            <th>From</th>
            <th>To</th>
          </tr>
        </thead>
        <tbody>
          {changes.map((change, index) => (
            <tr key={`${change.field}-${index}`}>
              <td className="font-medium">{change.field}</td>
              <td className="app-muted">{change.from}</td>
              <td>{change.to}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export const MessageCard = ({ message, isOwn }) => {
  const revisionPayload = parseRevisionMessage(message);
  const statusChange = message?.meta?.statusChange;
  const quoteMeta = message?.meta?.quote;

  return (
    <div
      className={`rounded-xl border px-3.5 py-3 ${
        isOwn ? "border-sky-500/25 bg-sky-500/10" : "app-surface"
      }`}
    >
      <div className="mb-1 flex items-baseline justify-between gap-2 text-xs">
        <span className="font-semibold">
          {isOwn ? "You" : personName(message?.user)}
        </span>
        <span className="app-muted whitespace-nowrap">
          {message?.createdAt ? normalizeDate(new Date(message.createdAt)) : ""}
        </span>
      </div>
      {statusChange ? (
        <div className="space-y-2 text-sm">
          <p className="font-semibold">Status updated</p>
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={statusChange.from} />
            <span className="app-muted text-xs">→</span>
            <StatusBadge status={statusChange.to} />
          </div>
        </div>
      ) : quoteMeta ? (
        <div className="space-y-1 text-sm">
          <p className="font-semibold">Quote attached</p>
          <a
            className="app-link break-all underline"
            href={quoteMeta.url}
            target="_blank"
            rel="noreferrer"
          >
            {quoteMeta.originalName}
          </a>
          {message.message && (
            <p className="whitespace-pre-wrap">{message.message}</p>
          )}
        </div>
      ) : revisionPayload ? (
        <div className="space-y-2 text-sm">
          <p className="font-semibold">
            Revision #{revisionPayload.revisionNumber || "-"} submitted on{" "}
            {revisionPayload.timestamp}
          </p>
          {revisionPayload.changes?.length > 0 ? (
            <RevisionChangesTable changes={revisionPayload.changes} />
          ) : (
            <p className="app-muted">No field changes detected.</p>
          )}
          {revisionPayload.notes && (
            <p className="whitespace-pre-wrap">
              <span className="font-semibold">Notes:</span>{" "}
              {revisionPayload.notes}
            </p>
          )}
        </div>
      ) : (
        <p className="whitespace-pre-wrap text-sm">{message?.message}</p>
      )}
    </div>
  );
};

export const ChatComposer = ({
  value,
  onChange,
  onSend,
  disabled,
  placeholder,
  buttonLabel,
  buttonClass = "bg-sky-600 hover:bg-sky-700",
}) => (
  <div className="mt-3 space-y-2">
    <textarea
      className="app-input w-full rounded-xl border px-3 py-2 text-sm"
      rows={2}
      aria-label={placeholder}
      placeholder={placeholder}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === "Enter" && !event.shiftKey) {
          event.preventDefault();
          if (!disabled) {
            onSend();
          }
        }
      }}
    />
    <div className="flex items-center justify-between gap-2">
      <p className="app-muted text-[11px]">
        Enter to send • Shift+Enter for a new line
      </p>
      <button
        type="button"
        className={`app-btn app-btn-sm text-white ${buttonClass}`}
        onClick={onSend}
        disabled={disabled}
      >
        {buttonLabel}
      </button>
    </div>
  </div>
);
